<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:link',
	name: '$t:blocks.input_list_m2m.name',
	description: '$t:blocks.input_list_m2m.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{ name: 'fieldId', label: '$t:blocks.common.props.field' },
		{ name: 'referencedItemKey', label: '$t:blocks.common.props.referenced_item' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
	],

	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { DefaultViewOptions } from '#layers/blocks/blocks/Listing.block.vue';
import type { UseM2MStateReturn } from '#layers/blocks/composables/relation/use-m2m-state';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { AnyItem, CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { FormSaveError } from '#layers/forms/public';
import type { ButtonProps } from '#layers/ui/public';
import { useOverlay } from '@nuxt/ui/composables';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { omit, toStructuredError } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import RelatedItemListCard from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import { useM2MDisplayItems } from '#layers/blocks/composables/relation/use-m2m-display-items';
import { useM2MRowActions } from '#layers/blocks/composables/relation/use-m2m-row-actions';
import { useM2MState } from '#layers/blocks/composables/relation/use-m2m-state';
import { getCollectionPrimaryKeyFields, isSameKey, pickByPrimitiveFields } from '#layers/data-model/public';
import { injectFormBlockContext, isCreatedItem, markCreatedItem, useDrawerFieldOverrides, useM2MPermissions, useRelationFormField } from '#layers/forms/public';
import { LazyCollectionDetailDrawer, LazyCollectionListingDrawer } from '#layers/listing/lazy';

type M2MActionProps = Partial<Omit<ButtonProps, 'onClick'>> & { onClick?: (m2mApi: UseM2MStateReturn) => void };

export interface InputListM2MBlockProps extends Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'> {
	fieldId: GlobalSingleRelationFieldId;
	referencedItemKey: CompositePrimaryKey;
	defaultView?: DefaultViewOptions;
	createAction?: M2MActionProps;
	editShown?: boolean;
	/** Pluralizable i18n key used for the item-count header (e.g. `common.n_files`). */
	countLabel?: string;
}

const props = withDefaults(defineProps<InputListM2MBlockProps>(), {
	editShown: true,
});

;

const { t } = useI18n();

const listCard = useTemplateRef('listCard');

const relationFieldContext = useRelationFormField<'to-many'>(props);
const { field, disabled, disabledNotice } = relationFieldContext;

const pageSize = ref(25);

const m2mState = useM2MState({
	...relationFieldContext,
	pageSize,
});
const {
	items,
	targetCollection,
	m2mStructure,
	connectTarget,
	createTarget,
	undoCreateTarget,
	updateTarget,
	editConnectedTarget,
	captureTargetEdits,
	isNewTargetCreate,
	getTargetFromJunction,
	getTargetKeyFromJunction,
	getTargetReferenceKeyFromJunction,
	connectedTargetsFilter,
	pageNumber,
	totalItemsCount,
	currentItemsCount,
	unpagedItemsCount,
	isLoading,
	junctionPrimaryKeyFields,
	targetReferenceKeyFields,
} = m2mState;

const permissions = useM2MPermissions({ m2mStructure });

const fieldOverrides = useDrawerFieldOverrides(field, targetCollection);

const createActionProps = computed<Partial<ButtonProps>>(() => ({
	...omit(props.createAction ?? {}, ['onClick']),
}));

const { displayItems } = useM2MDisplayItems(m2mState);
const { removeJunction, undoJunction } = useM2MRowActions(m2mState);

function findJunctionByTarget(targetData: AnyItem): AnyItem | undefined {
	return items.value.find((item) => {
		const target = getTargetFromJunction(item);
		return target && isSameKey(target, targetData);
	});
}

async function handleSelect() {
	if (!targetCollection.value) return;

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: targetCollection.value.id,
			selectionMode: 'multiple',
			selectionKeys: targetReferenceKeyFields.value ?? undefined,
			projectionKeys: getCollectionPrimaryKeyFields(targetCollection.value),
			filter: connectedTargetsFilter.value,
			defaultView: props.defaultView,
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result) {
		for (const target of result.selection) {
			connectTarget(pickByPrimitiveFields(target, targetReferenceKeyFields.value ?? []), {
				targetData: target,
			});
		}
	}
}

function handleRemove(targetData: AnyItem) {
	const junctionItem = findJunctionByTarget(targetData);
	if (junctionItem) removeJunction(junctionItem);
}

function handleUndo(targetData: AnyItem) {
	const junctionItem = findJunctionByTarget(targetData);
	if (junctionItem) undoJunction(junctionItem);
}

async function handleEdit(targetData: AnyItem) {
	if (!permissions.canEdit.value) return;
	if (!targetCollection.value) return;

	const junctionItem = findJunctionByTarget(targetData);
	if (!junctionItem) return;

	const targetItem = getTargetFromJunction(junctionItem);
	const primaryKey = getTargetKeyFromJunction(junctionItem);
	const referenceKey = getTargetReferenceKeyFromJunction(junctionItem);

	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: targetCollection.value.id,
			title: t('common.edit_item'),
			formMode: 'emit',
			itemEdits: targetItem ?? undefined,
			itemId: isNewTargetCreate(junctionItem) ? undefined : (primaryKey ?? undefined),
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result as { data: AnyItem } | undefined;

	if (payload) {
		if (isNewTargetCreate(junctionItem)) {
			// New target: undo the pending create, then recreate with the edited data.
			// This replaces the pending junction item entirely.
			undoCreateTarget(junctionItem);
			createTarget(markCreatedItem(payload.data, junctionItem));
		}
		else if (isCreatedItem(junctionItem)) {
			if (primaryKey) editConnectedTarget(primaryKey, payload.data);
		}
		else if (primaryKey && referenceKey) {
			updateTarget({ ...primaryKey, ...referenceKey }, payload.data);
		}
	}
}

async function handleCreate() {
	if (props.createAction?.onClick) {
		props.createAction.onClick(m2mState);
		return;
	}

	if (!targetCollection.value) return;

	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: targetCollection.value.id,
			title: t('common.create_item'),
			formMode: 'emit',
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result as { data: AnyItem } | undefined;

	if (payload) {
		createTarget(payload.data);
		listCard.value?.scrollToBottom();
	}
}

const formBlockContext = injectFormBlockContext();

// Persist edits to newly connected/uploaded targets after the parent (and its junction
// _connect) has saved, so a cancelled form never mutates the shared target record.
formBlockContext?.onSave(() => {
	const saveTargetEdits = captureTargetEdits();

	return async (): Promise<FormSaveError[]> => {
		try {
			await saveTargetEdits();
			return [];
		}
		catch (error) {
			return [{ message: t('blocks.input_list_m2m.target_update_failed'), source: toStructuredError(error) }];
		}
	};
});
</script>

<template>
	<DisplayTemplateConfigurationError
		v-if="!targetCollection?.id"
		:error="$t('blocks.common.relation_configuration_error')"
	/>
	<DisplayTemplateConfigurationError
		v-else-if="junctionPrimaryKeyFields?.length === 0"
		:error="$t('blocks.common.junction_missing_primary_key')"
	/>
	<RelatedItemListCard
		v-else
		ref="listCard"
		:items="displayItems"
		:count-label
		:collection-id="targetCollection?.id"
		:state-key="fieldId"
		:disabled
		:disabled-notice
		:readonly
		:aria-readonly="readonly"
		:show-edit="editShown"
		:create-action-props
		:pagination="{
			page: pageNumber,
			pageSize,
			totalItems: totalItemsCount,
			currentItems: currentItemsCount,
			unpagedItems: unpagedItemsCount,
		}"
		:loading="isLoading"
		:permissions="{
			canConnect: permissions.canConnect.value,
			canCreate: permissions.canCreate.value,
			canEdit: permissions.canEdit.value,
			canDisconnect: permissions.canDisconnect.value,
		}"
		show-create
		show-add-existing
		@select="handleSelect"
		@create="handleCreate"
		@edit="handleEdit"
		@remove="handleRemove"
		@undo="handleUndo"
		@update:page="pageNumber = $event"
		@update:page-size="pageSize = $event"
	/>
</template>
